import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import {
  Star, Gift, Trophy, TrendingUp, History,
  Crown, Sparkles, ChevronRight, Lock, Check
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LoyaltyDashboard = () => {
  const [loyaltyData, setLoyaltyData] = useState(null);
  const [rewards, setRewards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [redeeming, setRedeeming] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const [pointsRes, rewardsRes] = await Promise.all([
        axios.get(`${API_URL}/api/loyalty/my-points`, { headers }),
        axios.get(`${API_URL}/api/loyalty/rewards`, { headers })
      ]);

      setLoyaltyData(pointsRes.data);
      setRewards(rewardsRes.data.rewards || []);
    } catch (error) {
      console.error('Error loading loyalty data:', error);
      if (error.response?.status !== 404) {
        toast.error('Failed to load loyalty program');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRedeem = async (rewardId, rewardName) => {
    if (!window.confirm(`Redeem "${rewardName}"? This will deduct points from your balance.`)) {
      return;
    }

    setRedeeming(rewardId);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/loyalty/redeem`,
        { reward_id: rewardId },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success(response.data.message);
      loadData(); // Refresh data
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to redeem reward');
    } finally {
      setRedeeming(null);
    }
  };

  const getTierIcon = (tierName) => {
    switch (tierName) {
      case 'Platinum': return <Crown className="h-5 w-5" />;
      case 'Gold': return <Trophy className="h-5 w-5" />;
      case 'Silver': return <Star className="h-5 w-5" />;
      default: return <Star className="h-5 w-5" />;
    }
  };

  const getTierGradient = (tierName) => {
    switch (tierName) {
      case 'Platinum': return 'from-gray-300 via-gray-100 to-gray-300';
      case 'Gold': return 'from-yellow-400 via-yellow-300 to-yellow-500';
      case 'Silver': return 'from-gray-400 via-gray-200 to-gray-400';
      default: return 'from-amber-600 via-amber-500 to-amber-700';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  if (!loyaltyData?.enabled) {
    return (
      <Card className="max-w-md mx-auto">
        <CardContent className="pt-6 text-center">
          <Gift className="h-12 w-12 mx-auto mb-4 text-gray-300" />
          <p className="text-gray-600">Loyalty program is not available at this time.</p>
        </CardContent>
      </Card>
    );
  }

  const progressToNextTier = loyaltyData.next_tier
    ? ((loyaltyData.lifetime_points - (loyaltyData.current_tier?.min_points || 0)) /
       (loyaltyData.next_tier.min_points - (loyaltyData.current_tier?.min_points || 0))) * 100
    : 100;

  return (
    <div className="space-y-6" data-testid="loyalty-dashboard">
      {/* Points & Tier Card */}
      <Card className={`bg-gradient-to-r ${getTierGradient(loyaltyData.current_tier?.name)} text-white overflow-hidden`}>
        <CardContent className="pt-6">
          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center gap-2 mb-2">
                {getTierIcon(loyaltyData.current_tier?.name)}
                <span className="text-lg font-semibold">{loyaltyData.current_tier?.name} Member</span>
              </div>
              <div className="mb-4">
                <p className="text-sm opacity-80">Available Points</p>
                <p className="text-4xl font-bold">{loyaltyData.available_points?.toLocaleString()}</p>
              </div>
              <p className="text-sm opacity-80">
                Lifetime: {loyaltyData.lifetime_points?.toLocaleString()} points
              </p>
            </div>
            <div className="text-right">
              <Sparkles className="h-16 w-16 opacity-30" />
            </div>
          </div>

          {/* Progress to next tier */}
          {loyaltyData.next_tier && (
            <div className="mt-6 bg-white/20 rounded-lg p-3">
              <div className="flex justify-between text-sm mb-2">
                <span>{loyaltyData.current_tier?.name}</span>
                <span>{loyaltyData.next_tier.name}</span>
              </div>
              <Progress value={progressToNextTier} className="h-2 bg-white/30" />
              <p className="text-xs mt-2 text-center">
                {loyaltyData.points_to_next_tier?.toLocaleString()} points to {loyaltyData.next_tier.name}
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Tier Benefits */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-lg flex items-center gap-2">
            <Crown className="h-5 w-5 text-amber-500" />
            Your {loyaltyData.current_tier?.name} Benefits
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ul className="space-y-2">
            {loyaltyData.current_tier?.benefits?.map((benefit, i) => (
              <li key={i} className="flex items-center gap-2 text-sm">
                <Check className="h-4 w-4 text-green-500" />
                <span>{benefit}</span>
              </li>
            ))}
          </ul>
        </CardContent>
      </Card>

      {/* How to Earn Points */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-lg flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-green-500" />
            How to Earn Points
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-3">
            {Object.entries(loyaltyData.point_values || {}).slice(0, 6).map(([action, points]) => (
              <div key={action} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                <span className="text-sm text-gray-700 capitalize">
                  {action.replace(/_/g, ' ')}
                </span>
                <Badge variant="secondary" className="font-mono">+{points}</Badge>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Available Rewards */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Gift className="h-5 w-5 text-purple-500" />
            Available Rewards
          </CardTitle>
          <CardDescription>Redeem your points for exclusive rewards</CardDescription>
        </CardHeader>
        <CardContent>
          {rewards.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Gift className="h-12 w-12 mx-auto mb-3 text-gray-300" />
              <p>No rewards available yet</p>
            </div>
          ) : (
            <div className="space-y-3">
              {rewards.map((reward) => (
                <div
                  key={reward.id}
                  className={`flex items-center justify-between p-4 border rounded-lg transition-colors ${
                    reward.can_redeem ? 'hover:bg-purple-50 border-purple-200' : 'bg-gray-50 opacity-75'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${
                      reward.can_redeem ? 'bg-purple-100' : 'bg-gray-200'
                    }`}>
                      {reward.tier_locked ? (
                        <Lock className="h-5 w-5 text-gray-400" />
                      ) : (
                        <Gift className={`h-5 w-5 ${reward.can_redeem ? 'text-purple-600' : 'text-gray-400'}`} />
                      )}
                    </div>
                    <div>
                      <p className="font-medium">{reward.name}</p>
                      <p className="text-xs text-gray-500">{reward.description}</p>
                      {reward.tier_required && (
                        <Badge variant="outline" className="text-xs mt-1">
                          {reward.tier_required}+ only
                        </Badge>
                      )}
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-purple-600">{reward.points_required?.toLocaleString()} pts</p>
                    <Button
                      size="sm"
                      disabled={!reward.can_redeem || redeeming === reward.id}
                      onClick={() => handleRedeem(reward.id, reward.name)}
                      className={reward.can_redeem ? 'bg-purple-600 hover:bg-purple-700' : ''}
                    >
                      {redeeming === reward.id ? 'Redeeming...' : 'Redeem'}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Recent Activity */}
      {loyaltyData.recent_transactions?.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <History className="h-5 w-5 text-blue-500" />
              Recent Activity
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {loyaltyData.recent_transactions.slice(0, 5).map((tx, i) => (
                <div key={tx.id || i} className="flex items-center justify-between py-2 border-b last:border-0">
                  <div>
                    <p className="text-sm font-medium">{tx.description}</p>
                    <p className="text-xs text-gray-500">
                      {new Date(tx.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <span className={`font-mono font-bold ${tx.points > 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {tx.points > 0 ? '+' : ''}{tx.points}
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default LoyaltyDashboard;
